import React, { useEffect, useRef } from 'react';

import amplitude from 'amplitude-js';
import { BRAZE_BANNER_PLACEMENTS } from 'apps/auth/analytics/braze';
import BrazeBannerPlacement from 'apps/auth/components/braze/BrazeBannerPlacement';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { PanelRow } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import PaysendPlusStories from 'apps/auth/components/paysendPlus/PaysendPlusStories';
import LegalPopup from 'apps/auth/components/profile/LegalPopup';
import LogoutPopup from 'apps/auth/components/profile/LogoutPopup';
import ProfileFlogs from 'apps/auth/components/profile/ProfileFlogs';
import ProfileMenuItem from 'apps/auth/components/profile/ProfileMenuItem';
import TransferStatement from 'apps/auth/components/profile/statement/TransferStatement';
import BetaPromoStories from 'apps/auth/components/send/optIn/BetaPromoStories';
import { getHelpMobileIdParams } from 'apps/auth/components/utils/AmplitudeUtils';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import SupportPopup from 'apps/auth/components/utils/popup/SupportPopup';
import {
    GET_HELP_CLICKED,
    GET_TRANSFER_STATEMENT_CLICKED,
    INVITE_FRIENDS_CLICKED,
    OPT_IN_BETA_CLICKED,
    PROFILE_SCREEN_SHOWN,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { BUTTON_VARIANT } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { GENERAL, PROFILE } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { BRAZE_IAM_PLACEMENTS, BRAZE_IAM_SCREEN_EVENTS } from 'apps/auth/constants/braze/brazeIam';
import {
    AUTH_CARD_LINK,
    AUTH_GA_ACCOUNTS,
    AUTH_GA_CARDS,
    AUTH_PLUS_MAIN_PAGE,
    AUTH_PROFILE_BONUS,
    AUTH_PROFILE_FIELDS,
    AUTH_PROFILE_GA_LIMITS,
    AUTH_PROFILE_INVITE,
    AUTH_PROFILE_LIMITS,
    AUTH_PROFILE_SETTINGS,
    AUTH_REQUEST_MAIN_LINK,
    AUTH_SEND_MAIN_LINK,
    HELP_MOBILE,
} from 'apps/auth/constants/UrlConstants';
import { getLangCountryPrefix, toLowerCase } from 'apps/auth/helpers/utils';
import { useApp, useAuth, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { useBrazeIamScreen } from 'apps/auth/hooks/useBrazeIamScreen';
import { showPromoModal } from 'apps/auth/services/promoModalService';

const ProfilePage = () => {
    const popupRef = useRef(null);
    const popupFullwidthRef = useRef(null);
    const modalRef = useRef(null);
    const {
        sideBarMenu_cards,
        sideBarMenu_accounts,
        sideBarMenu_cardsAndAccounts,
        profileMenuBonus,
        profileMenuDetails,
        profileMenuLimits,
        inviteFriendsMenu,
        profileMenuSupport,
        profileMenuHelp,
        profileMenuLegal,
        termsUsInfo,
        logout,
        profileUpgrade,
        profileMenuSettings,
        profileMenuPlusSupport,
        getTransferStatement,
        optInBetaInfo,
    } = usePageMessages();
    const {
        formattedBonusAmount,
        bonusIso,
        canPayOnAccount,
        canPayOnCard,
        showPlusPromoSliders,
        appVersion,
        showProfilePlus,
        showBonus,
        showOptInNotificationToBetaPs3,
        promoSlidesOptInBeta,
        showInviteFriends,
        hasGlobalAccount,
        showSendingLimits,
        userCountry,
        profileCanBeUpgrade,
        profile_form: profileForm,
        plusPromoSliders,
        faqLabel,
    } = useExtra();
    const { currentSiteSection, country } = useApp();
    const { ballId, chatAvailable } = useAuth();
    const { lang } = useLanguage();

    const helpLink = `${getLangCountryPrefix(lang, country)}${HELP_MOBILE}${faqLabel ? `?popularLabel=${faqLabel}` : ''}`;
    const userCountryFlag = toLowerCase(userCountry?.code ?? userCountry);

    useEffect(() => {
        amplitude.getInstance().logEvent(PROFILE_SCREEN_SHOWN);
    }, []);

    useBrazeIamScreen(BRAZE_IAM_SCREEN_EVENTS.PROFILE, BRAZE_IAM_PLACEMENTS.PROFILE);

    const getBackUrl = () => {
        let url = AUTH_SEND_MAIN_LINK;
        if (currentSiteSection === 'send') {
            url = AUTH_SEND_MAIN_LINK;
        } else if (currentSiteSection === 'my_ga_cards') {
            url = AUTH_GA_CARDS;
        } else if (currentSiteSection === 'my_accounts') {
            url = AUTH_GA_ACCOUNTS;
        } else if (currentSiteSection === 'request') {
            url = AUTH_REQUEST_MAIN_LINK;
        }
        return url;
    };

    const getCardsTitle = () => {
        if (canPayOnAccount && !canPayOnCard) {
            return sideBarMenu_accounts;
        } else if (!canPayOnAccount && canPayOnCard) {
            return sideBarMenu_cards;
        } else {
            return sideBarMenu_cardsAndAccounts;
        }
    };

    const getName = (profileForm) => {
        if (profileForm?.userName && profileForm?.userLastName) {
            return `${profileForm.userName} ${profileForm.userLastName}`;
        } else {
            return null;
        }
    };

    const closePaysendPlusStories = () => {
        popupFullwidthRef?.current?.close();
    };

    const notNowButtonClick = () => {
        popupFullwidthRef?.current?.close();
    };

    const inviteButtonClick = () => {
        return false;
    };

    const bonusRightText = () => (
        <Text color="secondary">
            {formattedBonusAmount}
            <Text color="secondary" smallCaps>
                {' ' + toLowerCase(bonusIso)}
            </Text>
        </Text>
    );

    const renderPromoModal = () => {
        const showModal = showPromoModal(ballId, plusPromoSliders, 'paysend_plus_promo_info');
        if (showModal) {
            popupFullwidthRef?.current?.open(<PaysendPlusStories closeModal={closePaysendPlusStories} />);
        }
    };

    return (
        <>
            <CommonPage
                backUrl={getBackUrl()}
                rightHeaderComponent={
                    <DiskButton
                        iconName="dots"
                        onClick={() => {
                            popupRef.current?.open(<LogoutPopup logout={logout} popupRef={popupRef} fromFlow={PROFILE} />);
                        }}
                    />
                }
            >
                <Row indent="16px">
                    <BrazeBannerPlacement placementId={BRAZE_BANNER_PLACEMENTS.PROFILE} marginBottom="24px" />
                </Row>
                <Row indent="48px">
                    <Row indent="adaptive-16-24">
                        <DiskBadge
                            src={profileForm?.avatar ? profileForm.avatar : null}
                            iconName={!profileForm?.avatar ? 'person-48' : null}
                            diskSize="large"
                            flagName={userCountryFlag}
                        />
                    </Row>
                    <PanelRow>
                        <Text variant="div" color={'primary'} size="header" ellipsis>
                            {getName(profileForm)}
                        </Text>
                    </PanelRow>
                </Row>
                <Separator marginTop="0" marginTopDesktop="0" />
                {showProfilePlus && (
                    <ProfileMenuItem
                        iconName="plus-rounded"
                        to={!showPlusPromoSliders ? AUTH_PLUS_MAIN_PAGE : null}
                        onClick={() => {
                            if (showPlusPromoSliders) {
                                renderPromoModal();
                            }
                        }}
                        text={profileMenuPlusSupport}
                        rightText={<Badge caption="New" />}
                    />
                )}
                {showOptInNotificationToBetaPs3 && (
                    <>
                        <ProfileMenuItem
                            iconName="paysend-rounded"
                            onClick={() => {
                                amplitude.getInstance().logEvent(OPT_IN_BETA_CLICKED);
                                popupFullwidthRef.current?.open(
                                    <BetaPromoStories
                                        hasTopButton={promoSlidesOptInBeta?.not_now}
                                        sliders={promoSlidesOptInBeta}
                                        onTopButtonClick={notNowButtonClick}
                                        onBottomButtonClick={inviteButtonClick}
                                        popupFullwidthRef={popupFullwidthRef}
                                        modalRef={modalRef}
                                    />,
                                );
                            }}
                            text={optInBetaInfo}
                        />
                        <Separator />
                    </>
                )}
                {showBonus && (
                    <>
                        <ProfileMenuItem iconName="gift" to={AUTH_PROFILE_BONUS} text={profileMenuBonus} rightText={bonusRightText()} />
                        <Separator />
                    </>
                )}
                {/* todo add paysend plus condition to separator */}
                {showInviteFriends && (
                    <>
                        <ProfileMenuItem
                            iconName="people"
                            to={AUTH_PROFILE_INVITE}
                            text={inviteFriendsMenu}
                            onClick={() => {
                                amplitude.getInstance().logEvent(INVITE_FRIENDS_CLICKED, { [BUTTON_VARIANT]: GENERAL });
                            }}
                        />
                        <Separator />
                    </>
                )}
                <ProfileMenuItem iconName="person" to={AUTH_PROFILE_FIELDS} text={profileMenuDetails} />
                <ProfileMenuItem iconName="card" to={AUTH_CARD_LINK} text={getCardsTitle()} />
                {showSendingLimits && (
                    <ProfileMenuItem
                        iconName="speedometer"
                        to={hasGlobalAccount ? AUTH_PROFILE_GA_LIMITS : AUTH_PROFILE_LIMITS}
                        labelText={profileCanBeUpgrade ? profileUpgrade : null}
                        text={profileMenuLimits}
                    />
                )}
                <ProfileMenuItem
                    iconName="receipt"
                    onClick={() => {
                        amplitude.getInstance().logEvent(GET_TRANSFER_STATEMENT_CLICKED);
                        modalRef.current?.open(<TransferStatement modalRef={modalRef} />);
                    }}
                    text={getTransferStatement}
                />
                <ProfileMenuItem iconName="settings" to={AUTH_PROFILE_SETTINGS} text={profileMenuSettings} />
                <Separator />
                {!profileForm?.showSupportChat && !chatAvailable && (
                    <ProfileMenuItem
                        iconName="lifebuoy"
                        href={helpLink}
                        text={profileMenuHelp}
                        onClick={() => amplitude.getInstance().logEvent(GET_HELP_CLICKED, getHelpMobileIdParams(helpLink))}
                    />
                )}
                {(profileForm?.showSupportChat || chatAvailable) && (
                    <ProfileMenuItem
                        iconName="lifebuoy"
                        onClick={() => popupRef.current?.open(<SupportPopup popupRef={popupRef} />)}
                        text={profileMenuSupport}
                    />
                )}
                <ProfileMenuItem iconName="document" onClick={() => popupRef.current?.open(<LegalPopup popupRef={popupRef} />)} text={profileMenuLegal} />
                <Separator />
                {userCountry === 'us' && (
                    <Row indent="32px">
                        <DangerouslyHtml variant="div" type="text" color="secondary" dangerouslyHtml={termsUsInfo} />
                    </Row>
                )}
                <Row indent="32px">
                    <DangerouslyHtml variant="div" type="text" color="secondary" dangerouslyHtml={appVersion} />
                </Row>
                <ProfileFlogs />
            </CommonPage>
            <PopupNew ref={popupRef} />
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </>
    );
};

export default BasePage(ProfilePage);
